"use client";

import clsx from "clsx";
import parse, { domToReact, HTMLReactParserOptions } from "html-react-parser";
import { ArrowUpRightFromSquare } from "lucide-react";
import Link from "next/link";
import React from "react";
import { Button } from "../ui/Button";
import { H1, H2, H3, H4, P } from "../ui/Heading";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CustomComponent = React.ComponentType<any>;

interface ComponentMap {
  [key: string]: CustomComponent;
}

interface HTMLParserProps {
  htmlContent: string;
  customComponents?: ComponentMap;
  variant?: "sm" | "lg";
}

type StyleObject = {
  [key: string]: string | number;
};

export const HTMLParser: React.FC<HTMLParserProps> = ({
  htmlContent,
  customComponents = {},
  variant = "lg",
}) => {
  const toCamelCase = (str: string): string => {
    return str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
  };

  const parseStyles = (styleStr: string): StyleObject => {
    const styleObj: StyleObject = {};

    styleStr.split(";").forEach((style) => {
      if (style.trim()) {
        const [property, value] = style.split(":");
        const camelProperty = toCamelCase(property.trim());
        styleObj[camelProperty] = value.trim();
      }
    });

    return styleObj;
  };

  const defaultComponentMap: ComponentMap = {
    h1: ({ children }) => (
      <H1
        className={clsx("mt-3 sm:mt-5 mb-2 leading-7", {
          "!text-xl": variant === "sm",
        })}
      >
        {children}
      </H1>
    ),
    h2: ({ children }) => (
      <H2
        className={clsx("mt-3 sm:mt-5 mb-2 leading-7", {
          "!text-lg": variant === "sm",
        })}
      >
        {children}
      </H2>
    ),
    h3: ({ children }) => (
      <H3
        className={clsx("mt-3 sm:mt-5 mb-2 !font-normal leading-7", {
          "!text-lg sm:!text-lg": variant === "sm",
          "!text-xl sm:!text-2xl": variant === "lg",
        })}
      >
        {children}
      </H3>
    ),
    h4: ({ children }) => (
      <H4 className="mt-3 sm:mt-5 mb-2 leading-7">{children}</H4>
    ),
    div: ({ children }) => <div className="my-8">{children}</div>,
    p: ({ children }) => (
      <P
        className={clsx("py-1 !font-light", {
          "text-md !leading-5": variant === "sm",
          "text-lg !leading-6": variant === "lg",
        })}
      >
        {children}
      </P>
    ),
    ol: ({ children }) => <ol className="list-decimal">{children}</ol>,
    ul: ({ children }) => <ul className="list-disc">{children}</ul>,
    li: ({ children }) => (
      <li
        className={clsx("ml-4 !leading-6", {
          "text-md !leading-5": variant === "sm",
          "text-lg !leading-6": variant === "lg",
        })}
      >
        {children}
      </li>
    ),
    a: ({ children, href }) => {
      return (
        <Link className="my-1" href={href} target="_blank">
          <Button variant={"link"} size={"lg"} className="!text-lg ">
            {children}
            <ArrowUpRightFromSquare className="size-4.5" />
          </Button>
        </Link>
      );
    },
    ...customComponents,
  };

  const combinedComponentMap = { ...defaultComponentMap, ...customComponents };

  const parserOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (
        domNode &&
        Object.hasOwn(domNode, "name") &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        combinedComponentMap[(domNode as any).name]
      ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const Component = combinedComponentMap[(domNode as any).name];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const props: Record<string, any> = {};

        // Copy all attributes
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const [key, value] of Object.entries((domNode as any).attribs)) {
          if (key === "style") {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            props.style = parseStyles(value as any);
          } else {
            props[key] = value;
          }
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        props.children = domToReact((domNode as any).children, parserOptions);

        return <Component {...props} />;
      }
    },
  };

  return (
    <div className="html-parser-container">
      {parse(htmlContent, parserOptions)}
    </div>
  );
};
